/** @jsxImportSource @emotion/react */

import { Location } from 'modules/locations/domain/interfaces/location';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AppDispatch } from 'store/store';
import { UDListItem, UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import CategoryListPage from 'ud-ui/components/category/pages/category-list-page';
import { mapAddressToDadataArea } from 'ud-ui/components/dadata-address-autocomplete/infra/data-mappers/dadata-area.data-mapper';
import StadiumPlaceholderIcon from 'ud-ui/components/icon/stadium-placeholder-icon';
import Modal from 'ud-ui/components/modal';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { deleteLocation } from 'modules/locations/store/actions';
import { selectIsProcessing } from 'modules/locations/store/selectors';
import EmptyState from '../components/empty-state';
import { useFetchLocation } from '../hooks/use-fetch-location';
import { useLocationList } from '../hooks/use-location-list';

const naming = {
  searchPlaceholder: 'Поиск по названию локации',
  addButtonText: 'Добавить локацию',
};

function LocationsListPage() {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, isLoaded, locations, loadNextPage, onChangeFilter, filteredLocations } = useLocationList();
  const navigate = useNavigate();
  const { isProcessing } = useSelector(selectIsProcessing);
  // const { defaultOpen: openDeleteModal, close: closeDeleteModal, Modal: ModalDelete } = useModal();
  const { setHeaderContent } = useMainLayout();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const openDeleteModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  const closeDeleteModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  useFetchLocation();

  const onClickCard = (location: Location) => {
    if (!location) return;
    navigate(Pages.LOCATIONS.builders.edit(location.id));
  };

  const handleDeleteLocation = async () => {
    try {
      if (!selectedLocation) return;
      await dispatch(deleteLocation({ id: selectedLocation.id })).unwrap();
      closeDeleteModal();
    } catch (error) {
      console.error('Failed to delete location:', error);
      toast.error('Не удалось удалить локацию, возможно она используется в турнирах/матчах');
    }
  };

  const onClickDelete = useCallback(
    (location: Location) => {
      setSelectedLocation(location);
      openDeleteModal();
    },
    [openDeleteModal],
  );

  const handleCloseDeleteModal = useCallback(() => {
    closeDeleteModal();
    setSelectedLocation(null);
  }, [closeDeleteModal]);

  const handleCreateClick = useCallback(() => {
    navigate(Pages.LOCATIONS.CREATE);
  }, [navigate]);

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title="Лоакции" createButtonText="Добавить локацию" createButtonOnClick={handleCreateClick} />,
    );
    return () => setHeaderContent(null);
  }, [handleCreateClick, setHeaderContent]);

  return (
    <>
      {locations.length === 0 && isLoaded ? (
        <EmptyState />
      ) : (
        <CategoryListPage
          isLoading={isLoading}
          isLoaded={isLoaded}
          categoryName="locations"
          text={naming}
          getFiltered={onChangeFilter}
          getNextPages={loadNextPage}
          onCreateClick={() => navigate(Pages.LOCATIONS.CREATE)}
        >
          {(filteredLocations || locations).map((location, index) => {
            const address = location.address ? mapAddressToDadataArea(location.address).value : '';
            return (
              <UDListItem
                className="white"
                onClick={() => onClickCard(location)}
                image={location?.images[0]?.externalUrl ?? <StadiumPlaceholderIcon size={52} className="text-surfaceRomanSilver30" />}
                index={index + 1}
                title={location.name}
                subtitle={
                  <p style={{ fontWeight: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={address}>
                    {address}
                  </p>
                }
                actions={[
                  {
                    label: 'Удалить',
                    onClick: () => onClickDelete(location),
                    variant: 'secondary',
                    style: { width: 144 },
                  },
                ]}
              />
            );
          })}
        </CategoryListPage>
      )}

      <Modal show={isModalOpen} onClose={closeDeleteModal}>
        <Modal.Body className="flex-center flex-col gap-2">
          <UDText type="body-text" className="w-full">
            Удалить локацию?
          </UDText>
          <UDText type="subhead" className="w-full">
            Локация <strong>{selectedLocation?.name}</strong> будет безвозвратно удалёна
          </UDText>
        </Modal.Body>

        <Modal.Footer>
          <UDButton variant="primary" onClick={handleDeleteLocation} loading={isProcessing} stretch>
            Удалить
          </UDButton>
          <UDButton variant="secondary" onClick={handleCloseDeleteModal} disabled={isProcessing} stretch>
            Отмена
          </UDButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default LocationsListPage;
